import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';

import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap } from '@atlaskit/css';
import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import AttachmentIcon from '@atlaskit/icon/core/attachment';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import type { HoverEntityCardClickTarget } from '../entity-annotations/types';
import type { PromoteInferredProjectSectionCurrentUserQuery } from './__generated__/PromoteInferredProjectSectionCurrentUserQuery.graphql';
import messages from './inferredProjectCardMessages';
import type { InferredProjectPerson } from './parseInferredProjectContext';
import type { PromoteInferredProjectState } from './usePromoteInferredProject';

const styles = cssMap({
	actions: {
		display: 'grid',
		gap: token('space.100'),
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	},
	divider: {
		borderBlockStartColor: token('color.border'),
		borderBlockStartStyle: 'solid',
		borderBlockStartWidth: token('border.width'),
	},
	status: {
		minBlockSize: '32px',
	},
});

export type PromoteInferredProjectSectionProps = {
	contributors: readonly InferredProjectPerson[];
	inferredProjectAri: string;
	onClaimEligibility?: (canClaimProject: boolean | undefined) => void;
	onInteraction?: (targetType: HoverEntityCardClickTarget) => void;
	promote: (inferredProjectAri: string) => void;
	state: PromoteInferredProjectState;
};

export const PromoteInferredProjectSection = ({
	contributors,
	inferredProjectAri,
	promote,
	state,
	onInteraction,
	onClaimEligibility,
}: PromoteInferredProjectSectionProps): React.ReactNode => {
	const { formatMessage } = useIntl();
	const currentUser = useLazyLoadQuery<PromoteInferredProjectSectionCurrentUserQuery>(
		graphql`
			query PromoteInferredProjectSectionCurrentUserQuery {
				me {
					user {
						accountId
					}
				}
			}
		`,
		{},
		{ fetchPolicy: 'store-or-network' },
	);
	const [isCopied, setIsCopied] = useState(false);
	const viewerAri = currentUser.me?.user?.accountId
		? `ari:cloud:identity::user/${currentUser.me.user.accountId}`
		: undefined;
	const canPromote = Boolean(
		viewerAri && contributors.some((contributor) => contributor.ari === viewerAri),
	);
	useEffect(() => {
		onClaimEligibility?.(viewerAri ? canPromote && state.status !== 'promoted' : undefined);
	}, [viewerAri, canPromote, state.status, onClaimEligibility]);
	const handleCopy = useCallback(async () => {
		if (state.status !== 'promoted') {
			return;
		}

		onInteraction?.('copy_project_link');
		try {
			await navigator.clipboard.writeText(state.project.url);
			setIsCopied(true);
		} catch {
			// Clipboard access can be unavailable in embedded or non-secure contexts.
		}
	}, [state, onInteraction]);

	if (!canPromote) {
		return null;
	}

	return (
		<Box paddingBlockStart="space.100" xcss={styles.divider}>
			<Stack space="space.075">
				<Text align="center" color="color.text.subtle" size="small">
					{formatMessage(messages.claimProjectDescription)}
				</Text>
				{state.status === 'error' ? (
					<Inline alignBlock="center" alignInline="center" xcss={styles.status}>
						<Text color="color.text.danger" size="medium" weight="medium">
							{formatMessage(messages.claimProjectError)}
						</Text>
					</Inline>
				) : state.status === 'promoted' ? (
					state.showConfirmation ? (
						<Inline alignBlock="center" alignInline="center" space="space.050" xcss={styles.status}>
							<CheckCircleIcon color={token('color.icon.success')} label="" size="small" />
							<Text color="color.text.success" size="medium" weight="medium">
								{formatMessage(messages.projectConfirmed)}
							</Text>
						</Inline>
					) : (
						<Box xcss={styles.actions}>
							<LinkButton
								href={state.project.url}
								onClick={() => onInteraction?.('project')}
								onAuxClick={(event) => {
									if (event.button === 1) {
										onInteraction?.('project');
									}
								}}
								iconBefore={ArrowUpRightIcon}
								rel="noopener noreferrer"
								shouldFitContainer
								target="_blank"
							>
								{formatMessage(messages.viewProject)}
							</LinkButton>
							<Button iconBefore={AttachmentIcon} onClick={handleCopy} shouldFitContainer>
								{formatMessage(isCopied ? messages.linkCopied : messages.copyLink)}
							</Button>
						</Box>
					)
				) : (
					<Button
						isLoading={state.status === 'promoting'}
						onClick={() => promote(inferredProjectAri)}
						shouldFitContainer
					>
						{formatMessage(messages.claimProject)}
					</Button>
				)}
			</Stack>
		</Box>
	);
};
